import React from 'react';

import styled from 'styled-components';

import { isObject } from 'utils/helpers/object';

import { toInlineString } from './helpers';

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 12px;
`;

const TableHead = styled.th`
  text-align: left;
  font-size: 13px;
  font-family: var(--font-bold);
  color: var(--text-secondary);
  border-bottom: 1px solid var(--line-gray);
  padding: 8px 10px;
`;

const TableCell = styled.td`
  vertical-align: top;
  font-size: 14px;
  color: var(--text-primary);
  border-bottom: 1px solid var(--line-gray);
  padding: 10px;
  line-height: 1.5;
`;

const capitalizeFirst = (value: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);

const normalizeObjectRow = (row: unknown): Record<string, any> | null => {
  if (isObject(row)) {
    return row;
  }

  if (typeof row === 'string') {
    const trimmed = row.trim();
    if (!trimmed) {
      return null;
    }

    try {
      const parsed = JSON.parse(trimmed);
      if (isObject(parsed)) {
        return parsed;
      }
    } catch (e) {
      return null;
    }
  }

  return null;
};

export const canRenderDynamicTableValue = (value: unknown): boolean =>
  Array.isArray(value) &&
  value.some((item) => {
    if (isObject(item)) {
      return true;
    }

    if (typeof item === 'string') {
      try {
        const parsed = JSON.parse(item);
        return isObject(parsed);
      } catch (e) {
        return false;
      }
    }

    return false;
  });

type Props = {
  value: unknown;
};

const RecommendationDynamicTable = ({ value }: Props) => {
  if (!Array.isArray(value) || !canRenderDynamicTableValue(value)) {
    return null;
  }

  const rows = value.map(normalizeObjectRow).filter(Boolean) as Record<string, any>[];
  if (rows.length === 0) {
    return null;
  }

  const columns = rows.reduce((acc, row) => {
    Object.keys(row).forEach((key) => {
      if (!acc.includes(key)) {
        acc.push(key);
      }
    });
    return acc;
  }, [] as string[]);

  return (
    <Table>
      <thead>
        <tr>
          {columns.map((column: string) => (
            <TableHead key={column}>{capitalizeFirst(column)}</TableHead>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={index}>
            {columns.map((column: string) => (
              <TableCell key={`${index}-${column}`}>{toInlineString(row[column])}</TableCell>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

export default RecommendationDynamicTable;
